import type { Meeting } from '../../lib/meetingLogApi';
import {
  useAcceptTaskSuggestion,
  useDismissTaskSuggestion,
  useGenerateTaskSuggestions,
  useMeetingTaskSuggestions,
  useRestoreTaskSuggestion,
} from '../../hooks/useMeetingLog';
import { TaskSuggestionsPanel } from './TaskSuggestionsPanel';

/**
 * "Sugestii de sarcini" on a meeting: the panel (TaskSuggestionsPanel) wired
 * to the meeting's transcript — its suggestions, and the four decisions on
 * /api/meeting-log/:id/task-suggestions.
 */
export interface MeetingTaskSuggestionsProps {
  meeting: Meeting;
  /** Hide the decide/regenerate controls (no canEditNotes). */
  readOnly?: boolean;
}

export function MeetingTaskSuggestions({ meeting, readOnly = false }: MeetingTaskSuggestionsProps) {
  // Suggestions are written at the very end of the transcript pipeline, so
  // keep polling while any note is still being processed.
  const processing = meeting.meetingNotes.some(
    (n) => n.ingestStatus === 'PENDING' || n.ingestStatus === 'PROCESSING',
  );
  const hasNotes = meeting.meetingNotes.length > 0;
  const { data: suggestions = [], isLoading } = useMeetingTaskSuggestions(meeting.id, processing);
  const generateMutation = useGenerateTaskSuggestions(meeting.id);
  const acceptMutation = useAcceptTaskSuggestion(meeting.id);
  const dismissMutation = useDismissTaskSuggestion(meeting.id);
  const restoreMutation = useRestoreTaskSuggestion(meeting.id);

  return (
    <TaskSuggestionsPanel
      suggestions={suggestions}
      isLoading={isLoading}
      processing={processing}
      hasTranscript={hasNotes}
      readOnly={readOnly}
      actions={{
        generate: () => generateMutation.mutateAsync(undefined),
        generating: generateMutation.isPending,
        accept: (id, input) => acceptMutation.mutateAsync({ id, input }),
        dismiss: (id) => dismissMutation.mutateAsync(id),
        restore: (id) => restoreMutation.mutateAsync(id),
      }}
    />
  );
}
